/**
 * Places domain — list/summary fetchers, async export helpers, and
 * entity-type re-exports.
 *
 * Onboards the `google_places` backend (lodging + convenience chains and
 * doors) to the generated OpenAPI client. The list/summary endpoints are
 * exposed as plain fetcher functions — the generic `DECommonTable` wrapper
 * owns the React Query key (`LODGING_TABLE_KEY` etc.), so these mirror the
 * old `api.ts` signatures exactly and stay drop-in. The async v2 exports
 * are imperative helpers called from the export modals. Detail hooks live
 * in `./hooks`.
 *
 * Entity + pagination types are re-exported from the curated `models/`
 * definitions, not the generated ones: the hand-written models are richer
 * (the generated door-proxy serializer-method fields degrade to
 * `Record<string, unknown>[]`) and the table column defs already depend on
 * them, so this keeps consumer types unchanged.
 */

import type {
  PaginatedResponse,
  StandardSummaryResponse,
} from '../../models/paginated-response'
import type {
  ConvenienceChainProxy,
  LodgingChainProxy,
} from '../../models/google_place_chain'
import type {
  ConvenienceDoorProxy,
  LodgingDoorProxy,
} from '../../models/google_place_door'
import type {
  TargetExportV2Request,
  TargetExportV2Response,
} from '../downloadsApi'
import { exportConvenienceChainsV2 } from '../generated/clients/exportConvenienceChainsV2'
import { exportConvenienceDoorsV2 } from '../generated/clients/exportConvenienceDoorsV2'
import { exportLodgingChainsV2 } from '../generated/clients/exportLodgingChainsV2'
import { exportLodgingDoorsV2 } from '../generated/clients/exportLodgingDoorsV2'
import { getConvenienceDoorsSummary } from '../generated/clients/getConvenienceDoorsSummary'
import { getConvenienceSummary as getConvenienceSummaryClient } from '../generated/clients/getConvenienceSummary'
import { getLodgingDoorsSummary } from '../generated/clients/getLodgingDoorsSummary'
import { getLodgingSummary as getLodgingSummaryClient } from '../generated/clients/getLodgingSummary'
import { listConvenience } from '../generated/clients/listConvenience'
import { listConvenienceDoors } from '../generated/clients/listConvenienceDoors'
import { listLodging } from '../generated/clients/listLodging'
import { listLodgingDoors } from '../generated/clients/listLodgingDoors'

// ── Entity + pagination type re-exports ─────────────────────────────

export type {
  ConvenienceChainProxy,
  LodgingChainProxy,
} from '../../models/google_place_chain'
export type {
  ConvenienceDoorProxy,
  LodgingDoorProxy,
} from '../../models/google_place_door'

/**
 * Loose filter record. The tables build an arbitrary filter object via
 * `common_table_actions.ts`; the generated query-param types only cover
 * the documented cross-cutting params, so we accept a record and let the
 * generated client serialize it.
 */
type PlacesListParams = Record<string, unknown>

/** A generated GET client function (list or summary). */
type GeneratedGetClient = (params?: never) => Promise<unknown>

/**
 * Wrap a generated list/summary GET client as a loose-param fetcher typed
 * to the curated `models/` response shape. The runtime response is
 * unchanged — only the consumer-facing type is pinned.
 *
 * @param clientFn - The generated client function to wrap.
 * @returns A fetcher accepting a loose filter record.
 */
function placesGetFetcher<TResponse>(
  clientFn: GeneratedGetClient
): (params?: PlacesListParams) => Promise<TResponse> {
  return (params = {}) => clientFn(params as never) as Promise<TResponse>
}

/** A generated POST export client function. */
type GeneratedExportClient = (data: never) => Promise<unknown>

/**
 * Wrap a generated `export/v2/` POST client as an imperative export
 * starter returning the shared export-receipt type, so the export modals
 * need only an import-source swap.
 *
 * @param clientFn - The generated export client function to wrap.
 * @returns A starter accepting the shared `TargetExportV2Request`.
 */
function placesExportStarter(
  clientFn: GeneratedExportClient
): (data: TargetExportV2Request) => Promise<TargetExportV2Response> {
  return (data) => clientFn(data as never) as Promise<TargetExportV2Response>
}

// ── List + summary fetchers ─────────────────────────────────────────

export const getLodgingList =
  placesGetFetcher<PaginatedResponse<LodgingChainProxy>>(listLodging)
export const getLodgingSummary = placesGetFetcher<StandardSummaryResponse>(
  getLodgingSummaryClient
)
export const getLodgingDoorList =
  placesGetFetcher<PaginatedResponse<LodgingDoorProxy>>(listLodgingDoors)
export const getLodgingDoorSummary = placesGetFetcher<StandardSummaryResponse>(
  getLodgingDoorsSummary
)
export const getConvenienceList =
  placesGetFetcher<PaginatedResponse<ConvenienceChainProxy>>(listConvenience)
export const getConvenienceSummary = placesGetFetcher<StandardSummaryResponse>(
  getConvenienceSummaryClient
)
export const getConvenienceDoorList =
  placesGetFetcher<PaginatedResponse<ConvenienceDoorProxy>>(
    listConvenienceDoors
  )
export const getConvenienceDoorSummary =
  placesGetFetcher<StandardSummaryResponse>(getConvenienceDoorsSummary)

// ── Async v2 export helpers ─────────────────────────────────────────
//
// The export modals call these from an `onSubmit` handler and poll
// `getDownloadStatus(request_id)` (which stays in `downloadsApi`).

export const startLodgingChainsExportV2 = placesExportStarter(
  exportLodgingChainsV2
)
export const startLodgingDoorsExportV2 =
  placesExportStarter(exportLodgingDoorsV2)
export const startConvenienceChainsExportV2 = placesExportStarter(
  exportConvenienceChainsV2
)
export const startConvenienceDoorsExportV2 = placesExportStarter(
  exportConvenienceDoorsV2
)
